/**
 * Bot Partners — the admin view of the Bot Partner program (plan PR 10;
 * nurev-api services/partnerLifecycle + services/partnerDormancy).
 *
 * A partnership is a Discord server connected to a bot_partner app. When
 * the bot leaves a server, its earnings pause and the owner has 30 days to
 * reconnect; after that the partnership lapses: the app's installs lose
 * their lifetime share and the app goes back to standard terms.
 *
 *   - Partnerships tab: every partnership with its app, publisher, status,
 *     reconnect window and terms; the product server's configuration.
 *     Actions: Restore (a lapse made in error, once per lapse) and Pilot
 *     Partner on / off (the app's terms).
 *   - Absorbed balances tab: balances the dormancy rule moved to master's,
 *     whether the rule is armed and the payout rail up, and Give back.
 *
 * Every action asks for confirmation and takes an optional note; the API
 * writes it to the audit log with the change.
 */
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import {
  Absorption,
  PARTNERSHIP_STATUSES,
  Partnership,
  PartnershipStatus,
  apiErrorCode
} from '@/api/botPartners';
import {
  useAbsorptions,
  usePartnerships,
  useRestorePartnership,
  useReverseAbsorption,
  useSetPilotPartner
} from '@/hooks/useBotPartners';
import {
  ActionButton,
  Card,
  Pagination,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime, fmtNum } from '@/lib/format';

// The program's two tiers (nurev-api CONSTS.BOT_PARTNER); the API sets the
// numbers, these are for the confirmation copy only.
const STANDARD_ACTIVE_PCT = 10;
const PILOT_ACTIVE_PCT = 25;
const WINDOW_DAYS = 30;
const DORMANCY_DAYS = 180;

const PAGE_SIZE = 50;
const DAY_MS = 24 * 60 * 60 * 1000;
const OBJECT_ID = /^[a-f0-9]{24}$/i;

const STATUS_BADGE: Record<PartnershipStatus, string> = {
  active: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  disconnected: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  paused: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  lapsed: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  error: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30',
  pending_setup: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30'
};

const STATUS_OPTIONS: Array<{ value: PartnershipStatus | ''; label: string }> =
  [
    { value: '', label: 'All statuses' },
    ...PARTNERSHIP_STATUSES.map(s => ({
      value: s,
      label: s === 'pending_setup' ? 'Pending setup' : cap(s)
    }))
  ];

const ENDED_REASON: Record<string, string> = {
  removed: 'bot removed',
  disconnected: 'owner disconnected',
  account_closed: 'account closed',
  reconciled: 'found gone at startup'
};

// The API's refusal codes, in words.
const ERROR_TEXT: Record<string, string> = {
  NOT_FOUND: 'Not found. It may have changed since the list loaded; refresh.',
  NOTHING_TO_RESTORE:
    'Nothing to restore: this lapse was already restored, or the app is no longer in the program.',
  LAPSE_IN_PROGRESS:
    'This lapse hasn’t finished processing. The daily lifecycle job completes it; try again after it runs.',
  PARTNERSHIP_MOVED:
    'The server has been connected to a different app since this lapse.',
  PARTNERSHIP_DISCONNECTED:
    'The server is disconnected again. Restore once the owner has reconnected.',
  SERVER_HELD_BY_ANOTHER_PARTNER: 'Another partner holds this server now.',
  ALREADY_REVERSED: 'This balance has already been given back.',
  INVALID_PILOT_PARTNER: 'The API refused the Pilot Partner value.'
};

function errorText(e: unknown): string {
  const code = apiErrorCode(e);
  if (!code) return 'Request failed. Check the API logs.';
  return ERROR_TEXT[code] || `Refused: ${code}`;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function usd(n: number | null | undefined): string {
  return Number(n || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  });
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${fmtNum(n)} ${n === 1 ? one : many}`;
}

/** The status cell's second line. */
function statusDetail(p: Partnership, now = Date.now()): string | null {
  const reason = p.endedReason
    ? ENDED_REASON[p.endedReason] || p.endedReason
    : null;
  if (p.status === 'active') {
    return p.connectedAt ? `Connected ${fmtDate(p.connectedAt)}` : null;
  }
  if (p.status === 'disconnected') {
    if (p.windowEndsAt) {
      const ends = new Date(p.windowEndsAt).getTime();
      if (ends > now) {
        const days = Math.ceil((ends - now) / DAY_MS);
        return `Reconnect by ${fmtDate(p.windowEndsAt)} (${plural(days, 'day')} left)${
          reason ? ` · ${reason}` : ''
        }`;
      }
      return `Window ended ${fmtDate(p.windowEndsAt)}; lapses at the next lifecycle run`;
    }
    return reason ? cap(reason) : null;
  }
  if (p.status === 'lapsed') {
    return `Ended ${fmtDate(p.lapsedAt || p.lastLapse?.at)}${reason ? ` · ${reason}` : ''}`;
  }
  return reason ? cap(reason) : null;
}

const TABS = [
  { id: 'partnerships', label: 'Partnerships' },
  { id: 'absorptions', label: 'Absorbed balances' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function BotPartnersScreen() {
  const [tab, setTab] = useState<Tab>('partnerships');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header>
        <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
          Bot Partners
        </h1>
        <p className="mt-1 text-xs text-nurev-muted">
          Discord servers in the Bot Partner program. A removed bot pauses a
          server&apos;s earnings for a {WINDOW_DAYS}-day reconnect window; after
          that the partnership lapses. Restore a lapse made in error, set Pilot
          Partner terms, and review balances the dormancy rule absorbed. Every
          action is audit-logged.
        </p>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'partnerships' ? <PartnershipsTab /> : <AbsorptionsTab />}
    </div>
  );
}

// ─── Partnerships ─────────────────────────────────────────────────────────

type PartnershipAction =
  | { kind: 'restore'; p: Partnership }
  | { kind: 'pilot'; p: Partnership; pilotPartner: boolean };

function PartnershipsTab() {
  const [status, setStatus] = useState<PartnershipStatus | ''>('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<PartnershipAction | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const list = usePartnerships({ status, q, page, limit: PAGE_SIZE });
  const docs = list.data?.docs ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const ps = list.data?.productServer;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Partnerships"
          value={fmtNum(list.data ? total : undefined)}
          sub={status ? `Status: ${status}` : 'All statuses'}
          loading={list.isLoading}
        />
        <Card className="p-4 sm:col-span-1 lg:col-span-2 lg:p-5">
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-nurev-muted lg:mb-2 lg:text-xs">
            Product server
          </p>
          {list.isLoading ? (
            <Skeleton className="h-7 w-48" />
          ) : ps?.configured ? (
            <>
              <p className="text-sm font-semibold text-emerald-300">
                Configured
              </p>
              <p className="mt-0.5 break-all text-[11px] text-nurev-muted">
                Guild <span className="font-mono">{ps.guildId}</span> · app{' '}
                <span className="font-mono">{ps.appId}</span>. Members of a
                removed server earn here until it&apos;s back.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-amber-300">
                Not configured
              </p>
              <p className="mt-0.5 text-[11px] text-nurev-muted">
                Members of a removed server stay where they are. Set{' '}
                <code>PRODUCT_SERVER_APP_ID</code> and{' '}
                <code>PRODUCT_SERVER_GUILD_ID</code> in the API secret to move
                them to the product server.
              </p>
            </>
          )}
        </Card>
      </div>

      {done && (
        <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 ring-1 ring-emerald-500/20">
          {done}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={status}
          onChange={v => {
            setStatus(v);
            setPage(1);
          }}
          options={STATUS_OPTIONS}
        />
        <input
          value={q}
          onChange={e => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="Search server name / guild ID…"
          className="min-w-[200px] flex-1 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        />
      </div>

      <Card className="overflow-hidden p-0">
        {list.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-sm text-red-300">
            {errorText(list.error)}
          </p>
        ) : docs.length === 0 ? (
          <p className="p-8 text-center text-sm text-nurev-muted">
            {status || q.trim()
              ? 'No partnerships match these filters.'
              : 'No partnerships yet. A server joins when its owner connects a Bot Partner app with /setup connect.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3">Server</th>
                  <th className="px-4 py-3">App</th>
                  <th className="px-4 py-3">Publisher</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Terms</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(p => (
                  <PartnershipRow
                    key={p._id}
                    p={p}
                    onRestore={() => {
                      setDone(null);
                      setAction({ kind: 'restore', p });
                    }}
                    onPilot={pilotPartner => {
                      setDone(null);
                      setAction({ kind: 'pilot', p, pilotPartner });
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {action?.kind === 'restore' && (
        <RestoreModal
          p={action.p}
          onClose={() => setAction(null)}
          onDone={msg => {
            setAction(null);
            setDone(msg);
          }}
        />
      )}
      {action?.kind === 'pilot' && (
        <PilotModal
          p={action.p}
          pilotPartner={action.pilotPartner}
          onClose={() => setAction(null)}
          onDone={msg => {
            setAction(null);
            setDone(msg);
          }}
        />
      )}
    </div>
  );
}

function PartnershipRow({
  p,
  onRestore,
  onPilot
}: {
  p: Partnership;
  onRestore: () => void;
  onPilot: (pilotPartner: boolean) => void;
}) {
  const terms = p.app?.terms;
  const detail = statusDetail(p);
  const lapse = p.lastLapse;
  return (
    <tr className="border-b border-nurev-border/50 align-top hover:bg-white/[0.02]">
      <td className="px-4 py-3">
        <p className="max-w-[220px] truncate text-nurev-text">
          {p.name || 'Unnamed server'}
        </p>
        <p className="font-mono text-[11px] text-nurev-muted">{p.guildId}</p>
      </td>
      <td className="px-4 py-3">
        {p.app ? (
          <>
            <p className="max-w-[180px] truncate text-nurev-text">
              {p.app.name || 'Untitled app'}
            </p>
            <p className="font-mono text-[11px] text-nurev-muted">
              {p.app._id}
            </p>
          </>
        ) : (
          <span className="text-nurev-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <Link
          to={`/publishers/${p.publisher._id}`}
          className="block max-w-[200px] truncate text-amber-300 hover:text-amber-200"
        >
          {p.publisher.email || p.publisher._id}
        </Link>
        {p.publisher.name && (
          <p className="max-w-[200px] truncate text-[11px] text-nurev-muted">
            {p.publisher.name}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${
            STATUS_BADGE[p.status] ||
            'bg-white/10 text-nurev-muted ring-1 ring-white/10'
          }`}
        >
          {p.status}
        </span>
        {detail && (
          <p className="mt-1 max-w-[260px] text-[11px] text-nurev-muted">
            {detail}
          </p>
        )}
        {lapse && (
          <p className="mt-1 max-w-[260px] text-[11px] text-nurev-muted">
            {lapse.restoredAt
              ? `Lapse of ${fmtDate(lapse.at)} restored ${fmtDate(lapse.restoredAt)}`
              : lapse.appEnded
                ? `Lapse ${fmtDate(lapse.at)}: ${plural(lapse.devices, 'install')} lost the lifetime share, ${plural(lapse.rehomed, 'member')} moved`
                : `Lapse ${fmtDate(lapse.at)}: the app stayed live elsewhere`}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {terms ? (
          <>
            <p className="text-nurev-text">
              {terms.activeSharePct}% active · {terms.originalReferrerPct}%
              lifetime
            </p>
            {terms.pilotPartner && (
              <span
                className="mt-1 inline-block rounded bg-sky-500/20 px-2 py-0.5 text-[10px] font-medium text-sky-300 ring-1 ring-sky-500/30"
                title={
                  terms.grandfatheredAt
                    ? `Pilot since ${fmtDate(terms.grandfatheredAt)}`
                    : undefined
                }
              >
                Pilot Partner
              </span>
            )}
          </>
        ) : (
          <span className="text-nurev-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap justify-end gap-2">
          {p.canRestore && (
            <ActionButton size="sm" variant="positive" onClick={onRestore}>
              Restore
            </ActionButton>
          )}
          {terms && (
            <ActionButton
              size="sm"
              variant={terms.pilotPartner ? 'neutral' : 'info'}
              onClick={() => onPilot(!terms.pilotPartner)}
            >
              {terms.pilotPartner ? 'Set standard' : 'Set Pilot'}
            </ActionButton>
          )}
        </div>
      </td>
    </tr>
  );
}

function RestoreModal({
  p,
  onClose,
  onDone
}: {
  p: Partnership;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const restore = useRestorePartnership();
  const [note, setNote] = useState('');
  const lapse = p.lastLapse;
  const server = p.name || p.guildId;

  const submit = () =>
    restore.mutate(
      { id: p._id, note },
      {
        onSuccess: r => {
          const parts = [`Restored ${server}.`];
          if (r.windowEndsAt) {
            parts.push(
              `The owner can reconnect until ${fmtDate(r.windowEndsAt)}.`
            );
          }
          if (r.devicesRestored) {
            parts.push(
              `${plural(r.devicesRestored, 'install')} got the lifetime share back.`
            );
          }
          if (r.termsRestored) parts.push('The app’s terms are back.');
          if (r.membersRestored) {
            parts.push(`${plural(r.membersRestored, 'member')} moved back.`);
          }
          onDone(parts.join(' '));
        }
      }
    );

  return (
    <ActionModal
      title={`Restore ${server}`}
      onClose={onClose}
      confirmLabel={restore.isPending ? 'Restoring…' : 'Restore'}
      confirmVariant="positive"
      pending={restore.isPending}
      error={restore.isError ? errorText(restore.error) : null}
      note={note}
      setNote={setNote}
      notePlaceholder="Why, e.g. the bot was kicked during a Discord outage"
      onConfirm={submit}
    >
      <p>
        Undo the lapse of {fmtDate(lapse?.at)} for{' '}
        <span className="text-nurev-text">{p.app?.name || 'this app'}</span>.
        Use it for a lapse made in error; each lapse can be restored once.
      </p>
      <ul className="mt-3 list-disc space-y-1 pl-5">
        {lapse?.appEnded ? (
          <>
            <li>
              {plural(lapse.devices, 'install')} get the lifetime share back.
            </li>
            <li>The app gets back the terms it had before the lapse.</li>
            {p.status === 'active' && (
              <li>
                The owner has already reconnected, so the members moved away at
                the removal come back now.
              </li>
            )}
          </>
        ) : (
          <li>
            The app was still live on another server at the lapse, so its
            installs and terms weren&apos;t changed
            {p.status === 'active'
              ? '. The owner has already reconnected: restoring only marks the lapse restored.'
              : '.'}
          </li>
        )}
        {p.status === 'lapsed' && (
          <li>
            The server goes back to disconnected with a new {WINDOW_DAYS}-day
            window. The owner reconnects with <code>/setup connect</code>, and
            the members moved away at the removal come back then.
          </li>
        )}
      </ul>
    </ActionModal>
  );
}

function PilotModal({
  p,
  pilotPartner,
  onClose,
  onDone
}: {
  p: Partnership;
  pilotPartner: boolean;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const setPilot = useSetPilotPartner();
  const [note, setNote] = useState('');
  const app = p.app?.name || 'this app';
  const current = p.app?.terms.activeSharePct;

  const submit = () =>
    setPilot.mutate(
      { id: p._id, pilotPartner, note },
      {
        onSuccess: r =>
          onDone(
            `${app}: ${r.terms.activeSharePct}% active share${
              r.terms.pilotPartner ? ' (Pilot Partner)' : ' (standard)'
            }.`
          )
      }
    );

  return (
    <ActionModal
      title={
        pilotPartner
          ? `Make ${app} a Pilot Partner`
          : `Set ${app} to standard terms`
      }
      onClose={onClose}
      confirmLabel={
        setPilot.isPending
          ? 'Saving…'
          : pilotPartner
            ? 'Set Pilot'
            : 'Set standard'
      }
      confirmVariant={pilotPartner ? 'info' : 'primary'}
      pending={setPilot.isPending}
      error={setPilot.isError ? errorText(setPilot.error) : null}
      note={note}
      setNote={setNote}
      notePlaceholder={
        pilotPartner
          ? 'Why, e.g. joined during the pilot'
          : 'Why, e.g. Pilot was set by mistake'
      }
      onConfirm={submit}
    >
      {pilotPartner ? (
        <p>
          The active share goes from {current ?? STANDARD_ACTIVE_PCT}% to{' '}
          <span className="text-nurev-text">{PILOT_ACTIVE_PCT}%</span> for life,
          dated today. The lifetime share on installs doesn&apos;t change.
        </p>
      ) : (
        <p>
          The active share goes from {current ?? PILOT_ACTIVE_PCT}% to{' '}
          <span className="text-nurev-text">{STANDARD_ACTIVE_PCT}%</span> and
          the Pilot date is cleared. Setting Pilot again later starts a new
          date. The lifetime share on installs doesn&apos;t change.
        </p>
      )}
      <p className="mt-3">
        Terms belong to the app, so this applies to every server connected to it
        (normally one), for earnings credited from now on.
      </p>
    </ActionModal>
  );
}

// ─── Absorbed balances ────────────────────────────────────────────────────

function AbsorptionsTab() {
  const [publisherId, setPublisherId] = useState('');
  const [giveBack, setGiveBack] = useState<Absorption | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const id = publisherId.trim();
  const idValid = id === '' || OBJECT_ID.test(id);
  const list = useAbsorptions({ publisherId: id, limit: 200 }, idValid);
  const docs = list.data?.docs ?? [];
  const open = docs.filter(a => !a.reversedAt);
  const openUsd = open.reduce((s, a) => s + (a.amountUsd || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard
          label="Dormancy rule"
          value={list.data ? (list.data.armed ? 'Armed' : 'Report only') : '—'}
          sub={
            list.data?.armed
              ? `Absorbs after ${DORMANCY_DAYS} days`
              : 'PARTNER_DORMANCY_ENABLED is off'
          }
          color={list.data?.armed ? 'text-amber-300' : 'text-nurev-muted'}
          loading={list.isLoading}
        />
        <StatCard
          label="Payout rail"
          value={list.data ? (list.data.railReady ? 'Up' : 'Down') : '—'}
          sub={
            list.data?.railReady
              ? 'Partners can be paid'
              : 'Nothing is absorbed while it’s down'
          }
          color={list.data?.railReady ? 'text-emerald-300' : 'text-red-300'}
          loading={list.isLoading}
        />
        <StatCard
          label="Absorbed, not given back"
          value={usd(openUsd)}
          sub={`${plural(open.length, 'balance')} in this list`}
          loading={list.isLoading}
        />
      </div>

      <p className="text-xs text-nurev-muted">
        When every partnership on a Bot Partner account has lapsed and the
        account claims nothing for {DORMANCY_DAYS} days (no payout, no new
        earnings), its withdrawable balance moves to the master account. Creator
        accounts are never touched. Giving a balance back moves it from
        master&apos;s and restarts the account&apos;s clock.
      </p>

      {done && (
        <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 ring-1 ring-emerald-500/20">
          {done}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={publisherId}
          onChange={e => setPublisherId(e.target.value)}
          placeholder="Filter by publisher ID…"
          className="min-w-[240px] flex-1 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 font-mono text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        />
        {!idValid && (
          <span className="text-xs text-red-300">
            A publisher ID is 24 hex characters.
          </span>
        )}
      </div>

      <Card className="overflow-hidden p-0">
        {list.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <p className="p-8 text-center text-sm text-red-300">
            {errorText(list.error)}
          </p>
        ) : docs.length === 0 ? (
          <p className="p-8 text-center text-sm text-nurev-muted">
            {id
              ? 'No absorbed balances for this publisher.'
              : 'No balances have been absorbed.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3">Absorbed</th>
                  <th className="px-4 py-3">Publisher</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Last lapse</th>
                  <th className="px-4 py-3">Clock started</th>
                  <th className="px-4 py-3 text-right">State</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(a => (
                  <tr
                    key={a._id}
                    className="border-b border-nurev-border/50 hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 text-nurev-text">
                      {fmtDateTime(a.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/publishers/${a.publisherId}`}
                        className="font-mono text-[12px] text-amber-300 hover:text-amber-200"
                      >
                        {a.publisherId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-nurev-text">
                      {usd(a.amountUsd)}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {fmtDate(a.lastLapsedAt)}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {fmtDate(a.clockStartedAt)}
                      <span className="text-[11px]">
                        {' '}
                        · {a.dormancyDays}-day rule
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {a.reversedAt ? (
                        <span className="text-[11px] text-nurev-muted">
                          Given back {fmtDate(a.reversedAt)}
                        </span>
                      ) : (
                        <ActionButton
                          size="sm"
                          variant="positive"
                          onClick={() => {
                            setDone(null);
                            setGiveBack(a);
                          }}
                        >
                          Give back
                        </ActionButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {docs.length >= 200 && (
        <p className="text-center text-[11px] text-nurev-muted">
          Showing the newest 200. Filter by publisher to see older ones.
        </p>
      )}

      {giveBack && (
        <GiveBackModal
          a={giveBack}
          onClose={() => setGiveBack(null)}
          onDone={msg => {
            setGiveBack(null);
            setDone(msg);
          }}
        />
      )}
    </div>
  );
}

function GiveBackModal({
  a,
  onClose,
  onDone
}: {
  a: Absorption;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const reverse = useReverseAbsorption();
  const [note, setNote] = useState('');

  const submit = () =>
    reverse.mutate(
      { id: a._id, note },
      {
        onSuccess: r =>
          onDone(
            `Gave ${usd(r.amountUsd)} back to ${a.publisherId}. Their ${DORMANCY_DAYS}-day clock starts again today.`
          )
      }
    );

  return (
    <ActionModal
      title={`Give back ${usd(a.amountUsd)}`}
      onClose={onClose}
      confirmLabel={reverse.isPending ? 'Giving back…' : 'Give back'}
      confirmVariant="positive"
      pending={reverse.isPending}
      error={reverse.isError ? errorText(reverse.error) : null}
      note={note}
      setNote={setNote}
      notePlaceholder="Why, e.g. the partner got in touch about it"
      onConfirm={submit}
    >
      <p>
        Moves {usd(a.amountUsd)} from the master account&apos;s balance back to
        publisher{' '}
        <span className="font-mono text-nurev-text">{a.publisherId}</span>,
        absorbed {fmtDate(a.createdAt)}. Their dormancy clock starts again
        today. A balance can be given back once.
      </p>
    </ActionModal>
  );
}

// ─── Confirmation modal ───────────────────────────────────────────────────

function ActionModal({
  title,
  children,
  onClose,
  onConfirm,
  confirmLabel,
  confirmVariant,
  pending,
  error,
  note,
  setNote,
  notePlaceholder
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmVariant: 'primary' | 'positive' | 'info';
  pending: boolean;
  error: string | null;
  note: string;
  setNote: (v: string) => void;
  notePlaceholder: string;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={() => !pending && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold text-nurev-text">{title}</h2>
          <button
            onClick={onClose}
            disabled={pending}
            className="text-nurev-muted hover:text-nurev-text disabled:opacity-30"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="text-sm text-nurev-muted">{children}</div>
        <label
          htmlFor="bot-partner-action-note"
          className="mb-2 mt-5 block text-[10px] font-medium uppercase tracking-wider text-nurev-muted"
        >
          Note for the audit log (optional)
        </label>
        <textarea
          id="bot-partner-action-note"
          value={note}
          onChange={e => setNote(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder={notePlaceholder}
          className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text focus:border-amber-500/50 focus:outline-none"
        />
        {error && (
          <p className="mt-3 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-300 ring-1 ring-red-500/20">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <ActionButton variant="neutral" onClick={onClose} disabled={pending}>
            Cancel
          </ActionButton>
          <ActionButton
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={pending}
          >
            {confirmLabel}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}
